import { differenceInCalendarDays, parseISO } from "date-fns";
import { Day } from "@/offering/day_utils";
import {
  DAYS_PER_WEEK,
  RecurrenceRule,
  RecurringAvailability,
  UnavailabilityReason,
} from "@/offering/recurrence";

export interface RecurringSkippedDate {
  date: Day;
  reason?: UnavailabilityReason;
}

export interface RecurringExpansion {
  anchor: Day;
  rule: RecurrenceRule;
  // The dates that will be quoted, ascending, the anchor first. Fewer than the
  // rule asked for only when the horizon ran out (`clamped`).
  booked: Day[];
  // Pattern dates strictly before the last booked date that were unavailable —
  // the same set the server derives as `skipped_dates`, so what the picker
  // shows as skipped is what the calendar event will exclude.
  skipped: RecurringSkippedDate[];
  // How many bookings this anchor and interval can hold inside the horizon:
  // the stepper's ceiling.
  maxCount: number;
  clamped: boolean;
  horizonEnd: Day;
  // The anchor itself is not bookable for this window, so there is nothing to
  // repeat. The picker offers only bookable anchors, so this is a guard for a
  // stale horizon rather than a state to design for.
  anchorUnavailable: boolean;
}

// The storefront twin of `Bookings::Recurrence::ExpandDates` plus
// `RecurrenceRequest#skipped_dates`: walk the horizon on the anchor's weekday,
// every `interval` weeks, collecting bookable dates until the count is met.
// Unavailable dates are skipped and the run extends past them
// (extend-to-fill), so the customer gets the number of bookings they asked for
// with a later end date rather than a shorter run.
//
// Pure. Every preset is `anchor + 7 * k * interval`, so this is a modulo test
// over the server's own list — there is no calendar reasoning here that could
// disagree with the server's validation of the same dates.
export function expandRecurrence(
  availability: RecurringAvailability,
  anchor: Day,
  rule: RecurrenceRule
): RecurringExpansion {
  const anchorDate = parseISO(anchor);
  const step = DAYS_PER_WEEK * rule.interval;

  const candidates = availability.dates.filter((d) => {
    const offset = differenceInCalendarDays(parseISO(d.date), anchorDate);
    return offset >= 0 && offset % step === 0;
  });

  const anchorVerdict = candidates.find((d) => d.date === anchor);
  const anchorUnavailable = !anchorVerdict?.bookable;
  const maxCount = anchorUnavailable
    ? 0
    : candidates.filter((d) => d.bookable).length;
  const target = Math.min(rule.count, maxCount);

  // The walk stops at the last booked date, so every unavailable candidate it
  // met lies strictly before it — which is exactly the server's skipped set.
  const booked: Day[] = [];
  const skipped: RecurringSkippedDate[] = [];
  for (const candidate of candidates) {
    if (booked.length >= target) break;
    if (candidate.bookable) {
      booked.push(candidate.date);
    } else {
      skipped.push({ date: candidate.date, reason: candidate.reason });
    }
  }

  return {
    anchor,
    rule,
    booked,
    skipped,
    maxCount,
    clamped: rule.count > maxCount,
    horizonEnd: availability.horizonEnd,
    anchorUnavailable,
  };
}
